import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import authService from '../../../authService';
import {
  fetchActivity,
  fetchActivitySuccess,
  fetchActivityFailure,
  fetchFilterOptions,
  fetchFilterOptionsSuccess,
  fetchFilterOptionsFailure,
  fetchAccessibleCustomers,
  fetchAccessibleCustomersSuccess,
  fetchAccessibleCustomersFailure,
  retryTask,
  retryTaskSuccess,
  retryTaskFailure,
  retryWorkflow,
  retryWorkflowDone,
  queueToast,
  fetchUserDisplayNames,
  fetchUserDisplayNamesSuccess,
  fetchUserDisplayNamesFailure
} from './activitySlice';
import {
  selectActivityFilters,
  selectActivityPagination,
  selectActivitySort,
  selectCustomerIds,
  selectUserDisplayNames,
} from './activitySelectors';
import {
  IActivityFilters,
  IActivityItem,
  IActivityPagination,
  IActivityQueryRequest,
  IActivityQueryResponse,
  IActivitySort,
  IFilterOptions,
  IFilterOptionsRequest,
  IUserDisplayNamesResponse,
} from './activityModels';
import { ACTIVITY_TOAST_MESSAGES as TOAST_MESSAGES, normalizeStatus, collectNewUserIds, buildDisplayNameMap, collectUncachedFilterUserIds } from '../utils';

export function* fetchActivitySaga(action: ReturnType<typeof fetchActivity>) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);

  try {
    const filters: IActivityFilters = yield select(selectActivityFilters);
    const pagination: IActivityPagination = yield select(selectActivityPagination);
    const sort: IActivitySort = yield select(selectActivitySort);
    const customerIds = action.payload?.customerIds ?? (action.payload?.customerId ? [action.payload.customerId] : []);
    const siteIds = action.payload?.siteIds ?? filters.siteIds;

    const requestBody: IActivityQueryRequest = {
      customerIds,
      siteIds,
      activity: filters.activity,
      statuses: filters.statuses,
      userIds: filters.userIds,
      searchText: filters.searchText,
      includeQueued: filters.includeQueued,
      pagination: {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
      },
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    };

    const response: Response = yield call(
      [authService, authService.authFetch],
      `${authService.BASE_URL}/api/v1/orchestrator/workflows/query`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      yield put(
        fetchActivityFailure({
          type: 'fetch',
          message: 'SERVER_ERROR',
          statusCode: response.status,
        })
      );
      return;
    }

    const data: IActivityQueryResponse = yield call([response, 'json']);

    const normalizedItems: IActivityItem[] = data.items.map(item => {
      const normalizedStatus = normalizeStatus(item.status as string);
      let tasks: IActivityItem['tasks'];
      if (Array.isArray(item.tasks)) {
        tasks = item.tasks.map(t => ({ ...t, status: normalizeStatus((t as any).status as string), retry: (t as any).canRetry ?? false }));
      } else {
        tasks = item.tasks;
      }
      return ({ ...item, status: normalizedStatus, tasks } as IActivityItem);
    });

    yield put(
      fetchActivitySuccess({
        items: normalizedItems,
        pagination: data.pagination,
      })
    );
  } catch (err) {
    const isTimeout = err instanceof Error && err.name === 'AbortError';
    yield put(
      fetchActivityFailure({
        type: 'fetch',
        message: isTimeout ? 'TIMEOUT' : 'SERVER_ERROR',
      })
    );
  } finally {
    clearTimeout(timeout);
    controller.abort();
  }
}

export function* fetchFilterOptionsSaga(action: ReturnType<typeof fetchFilterOptions>) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);

  try {
    const requestBody: IFilterOptionsRequest = action.payload;

    const response: Response = yield call(
      [authService, authService.authFetch],
      `${authService.BASE_URL}/api/v1/orchestrator/filters/values`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      }
    );

    clearTimeout(timeout);

    if (!response.ok) {
      yield put(fetchFilterOptionsFailure('SERVER_ERROR'));
      return;
    }

    const data: IFilterOptions = yield call([response, 'json']);
    yield put(fetchFilterOptionsSuccess(data));
  } catch (err) {
    clearTimeout(timeout);
    const isTimeout = err instanceof Error && err.name === 'AbortError';
    yield put(fetchFilterOptionsFailure(isTimeout ? 'TIMEOUT' : 'SERVER_ERROR'));
  }
}

export function* fetchAccessibleCustomersSaga(
  action: ReturnType<typeof fetchAccessibleCustomers>
) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);

  try {
    const { parentOrgId } = action.payload;
    const response: Response = yield call(
      [authService, authService.authFetch],
      `${authService.BASE_URL}/api/v3/organisations/${authService.OFFERING}/${parentOrgId}/accessible-customers`,
      {
        method: 'GET',
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      yield put(fetchAccessibleCustomersFailure('SERVER_ERROR'));
      return;
    }

    const data: { resources?: Array<{ id: string; name: string }> } = yield call([response, 'json']);
    const customers = (data?.resources ?? [])
      .map(item => ({ id: item.id, name: item.name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    yield put(fetchAccessibleCustomersSuccess({ parentOrgId, customers }));
  } catch (err) {
    const isTimeout = typeof err === 'object' && err !== null && 'name' in err && (err as { name?: string }).name === 'AbortError';
    yield put(fetchAccessibleCustomersFailure(isTimeout ? 'TIMEOUT' : 'SERVER_ERROR'));
  } finally {
    clearTimeout(timeout);
    controller.abort();
  }
}

function* callRetryTaskApi(taskId: string): Generator<any, boolean, any> {
  const response: Response = yield call(
    [authService, authService.authFetch],
    `${authService.BASE_URL}/api/v1/orchestrator/retryTask/${taskId}`,
    { method: 'POST' }
  );
  return response.ok;
}

export function* retryTaskSaga(action: ReturnType<typeof retryTask>) {
  const taskId = action.payload;
  try {
    const ok: boolean = yield call(callRetryTaskApi, taskId);
    if (!ok) {
      yield put(retryTaskFailure(taskId));
      yield put(queueToast({ type: 'error', message: TOAST_MESSAGES.retryError }));
    } else {
      yield put(retryTaskSuccess(taskId));
      yield put(queueToast({ type: 'success', message: TOAST_MESSAGES.retrySuccess }));
      const customerIds: string[] = yield select(selectCustomerIds);
      if (customerIds.length > 0) {
        yield put(fetchActivity({ customerIds }));
      }
    }
  } catch {
    yield put(retryTaskFailure(taskId));
    yield put(queueToast({ type: 'error', message: TOAST_MESSAGES.retryError }));
  }
}

function* retryOneTask(taskId: string): Generator<any, boolean, any> {
  try {
    return (yield call(callRetryTaskApi, taskId)) as boolean;
  } catch {
    return false;
  }
}

export function* retryWorkflowSaga(action: ReturnType<typeof retryWorkflow>) {
  const { workflowId, taskIds } = action.payload;

  const results: boolean[] = yield all(taskIds.map(taskId => call(retryOneTask, taskId)));
  const failedCount = results.filter(ok => !ok).length;

  yield put(retryWorkflowDone(workflowId));

  if (failedCount === taskIds.length) {
    yield put(queueToast({ type: 'error', message: TOAST_MESSAGES.retryAllFailed }));
  } else if (failedCount > 0) {
    yield put(queueToast({ type: 'error', message: TOAST_MESSAGES.retryPartialFailed }));
  } else {
    yield put(queueToast({ type: 'success', message: TOAST_MESSAGES.retryAllSuccess }));
  }

  const customerIds: string[] = yield select(selectCustomerIds);
  if (customerIds.length > 0) {
    yield put(fetchActivity({ customerIds }));
  }
}

export function* watchActivitySaga() {
  yield takeLatest(fetchActivity.type, fetchActivitySaga);
  yield takeLatest(fetchFilterOptions.type, fetchFilterOptionsSaga);
  yield takeLatest(fetchAccessibleCustomers.type, fetchAccessibleCustomersSaga);
  yield takeLatest(retryTask.type, retryTaskSaga);
  yield takeEvery(retryWorkflow.type, retryWorkflowSaga);
}

export function* resolveUserDisplayNamesSaga(newIds: string[]) {
  yield put(fetchUserDisplayNames(newIds));

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);

  try {
    const response: Response = yield call(
      [authService, authService.authFetch],
      `${authService.BASE_URL}/api/v1/users/display-names`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json-patch+json' },
        body: JSON.stringify(newIds),
        signal: controller.signal
      }
    );

    if (!response.ok) {
      yield put(fetchUserDisplayNamesFailure());
      yield put(queueToast({ type: 'error', message: TOAST_MESSAGES.userNamesError }));
      return;
    }

    const data: IUserDisplayNamesResponse = yield call([response, 'json']);
    const displayNameMap = buildDisplayNameMap(newIds, data.resources);
    yield put(fetchUserDisplayNamesSuccess(displayNameMap));
  } catch {
    yield put(fetchUserDisplayNamesFailure());
    yield put(queueToast({ type: 'error', message: TOAST_MESSAGES.userNamesError }));
  } finally {
    clearTimeout(timeout);
    controller.abort();
  }
}

export function* fetchUserDisplayNamesSaga(
  action: ReturnType<typeof fetchActivitySuccess>
) {
  const items: IActivityItem[] = action.payload.items;

  const alreadyResolved: Record<string, string> = yield select(
    selectUserDisplayNames
  );

  const newIds = collectNewUserIds(items, alreadyResolved);

  if (newIds.length === 0) {
    return;
  }

  yield call(resolveUserDisplayNamesSaga, newIds);
}

export function* watchUserDisplayNamesSaga() {
  yield takeEvery(fetchActivitySuccess.type, fetchUserDisplayNamesSaga);
}

export function* fetchFilterOptionsUserDisplayNamesSaga(
  action: ReturnType<typeof fetchFilterOptionsSuccess>
) {
  const alreadyResolved: Record<string, string> = yield select(selectUserDisplayNames);
  const newIds = collectUncachedFilterUserIds(action.payload, alreadyResolved);

  if (newIds.length === 0) {
    return;
  }

  yield call(resolveUserDisplayNamesSaga, newIds);
}

export function* watchFilterOptionsUserDisplayNamesSaga() {
  yield takeLatest(fetchFilterOptionsSuccess.type, fetchFilterOptionsUserDisplayNamesSaga);
}
